import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MunicipalityCard from '@/components/MunicipalityCard';
import StatsSummary from '@/components/StatsSummary';
import CountySelector from '@/components/CountySelector';
import SavingTipsCard from '@/components/SavingTipsCard';
import EnvironmentalImpactCalculator from '@/components/EnvironmentalImpactCalculator';
import { getMunicipalities, getAllMunicipalities, Municipality, savingTips, sumFees } from '@/lib/data';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';
import { getLatestYear } from '@/config/years';
import YearSelector from '@/components/YearSelector';
import { Search, Filter, ArrowUpDown, MapPin, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from "sonner";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

// Type for sorteringsalternativene
type SortOption = 'name-asc' | 'name-desc' | 'fee-asc' | 'fee-desc' | 'default';

const Index = () => {
  const navigate = useNavigate();
  const { enabledCategories } = useFeeCategories();
  // allMunicipalities holder alle kommuner for søk og filtrering
  const [allMunicipalities, setAllMunicipalities] = useState<Municipality[]>([]);
  // filteredMunicipalities holder kommunene som vises i grensesnittet basert på søk/filter
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<Municipality[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'allMunicipalities' | 'countyFiltered' | 'searchResults'>('allMunicipalities');
  const [sortOption, setSortOption] = useState<SortOption>('fee-desc');
  const [selectedYear, setSelectedYear] = useState<number>(() => getLatestYear());
  const municipalitiesPerPage = 12;
  
  // Gjenopprett tilstand fra localStorage når komponent lastes (ikke søk – det nullstilles ved omlasting)
  useEffect(() => {
    const savedState = localStorage.getItem('avgiftsview_state');
    if (savedState) {
      try {
        const {
          selectedCounty: savedCounty,
          viewMode: savedViewMode,
          currentPage: savedPage,
        } = JSON.parse(savedState);

        if (savedCounty !== null && savedCounty !== undefined) setSelectedCounty(savedCounty);
        if (savedViewMode &&
            (savedViewMode === 'allMunicipalities' ||
             savedViewMode === 'countyFiltered')) {
          setViewMode(savedViewMode);
        }
        if (savedPage) setCurrentPage(savedPage);
      } catch (e) {
        console.error('Feil ved gjenoppretting av tilstand:', e);
        localStorage.removeItem('avgiftsview_state');
      }
    }
  }, []);
  
  // Lagre søketilstand når den endres
  const saveState = () => {
    const stateToSave = {
      selectedCounty,
      viewMode: viewMode === 'searchResults' ? 'allMunicipalities' : viewMode,
      currentPage,
    };
    localStorage.setItem('avgiftsview_state', JSON.stringify(stateToSave));
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Bare hent data hvis vi ikke har noen kommuner eller trenger å oppdatere basert på søk/filter
        if (!allMunicipalities.length) {
          // Prøv å hente alle kommuner for søk og filtrering
          const allData = await getAllMunicipalities();
          setAllMunicipalities(allData);
          
          // Hvis vi har lagret søketerm eller fylke, kjør filtrering når data er lastet
          if (searchTerm && viewMode === 'searchResults') {
            const searchResults = performSearch(searchTerm, allData);
            const sortedResults = sortMunicipalities(searchResults, sortOption);
            setFilteredMunicipalities(sortedResults);
          } else if (selectedCounty && viewMode === 'countyFiltered') {
            const countyResults = performCountyFilter(selectedCounty, allData);
            const sortedResults = sortMunicipalities(countyResults, sortOption);
            setFilteredMunicipalities(sortedResults);
          } else {
            const sortedResults = sortMunicipalities(allData, sortOption);
            setFilteredMunicipalities(sortedResults);
            setViewMode('allMunicipalities');
          }
        }
      } catch (error) {
        console.error('Error fetching all municipalities:', error);
        
        try {
          // Fallback: Prøv å hente de grunnleggende kommunene hvis vi ikke kan hente alle
          const fallbackData = await getMunicipalities();
          setAllMunicipalities(fallbackData);
          setFilteredMunicipalities(fallbackData);
          toast.warning("Kunne ikke hente alle kommuner. Søk og filtrering vil være begrenset til utvalgte kommuner.");
        } catch (fallbackError) {
          console.error('Error fetching municipalities fallback:', fallbackError);
          toast.error("Kunne ikke hente kommunedata. Prøv igjen senere.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Separat useEffect for å håndtere endringer i søketerm, selectedCounty, og viewMode
  useEffect(() => {
    // Bare oppdater filtreringslogikk hvis vi har data
    if (allMunicipalities.length > 0) {
      if (searchTerm && viewMode === 'searchResults') {
        const searchResults = performSearch(searchTerm, allMunicipalities);
        const sortedResults = sortMunicipalities(searchResults, sortOption);
        setFilteredMunicipalities(sortedResults);
      } else if (selectedCounty && viewMode === 'countyFiltered') {
        const countyResults = performCountyFilter(selectedCounty, allMunicipalities);
        const sortedResults = sortMunicipalities(countyResults, sortOption);
        setFilteredMunicipalities(sortedResults);
      } else if (viewMode === 'allMunicipalities') {
        const sortedResults = sortMunicipalities(allMunicipalities, sortOption);
        setFilteredMunicipalities(sortedResults);
      }
    }
  }, [searchTerm, selectedCounty, viewMode, sortOption, allMunicipalities.length, enabledCategories]);

  // Funksjon for å sortere kommuner
  const sortMunicipalities = (municipalities: Municipality[], sortBy: SortOption = 'fee-desc'): Municipality[] => {
    const municipalitiesToSort = [...municipalities];
    
    switch (sortBy) {
      case 'name-asc':
        return municipalitiesToSort.sort((a, b) => a.name.localeCompare(b.name, 'nb-NO'));
      case 'name-desc':
        return municipalitiesToSort.sort((a, b) => b.name.localeCompare(a.name, 'nb-NO'));
      case 'fee-asc':
        return municipalitiesToSort.sort((a, b) => {
          const totalA = sumFees(a.currentFees, enabledCategories);
          const totalB = sumFees(b.currentFees, enabledCategories);
          return totalA - totalB;
        });
      case 'fee-desc':
      default:
        return municipalitiesToSort.sort((a, b) => {
          const totalA = sumFees(a.currentFees, enabledCategories);
          const totalB = sumFees(b.currentFees, enabledCategories);
          return totalB - totalA;
        });
    }
  };

  // Handle sortering
  const handleSortChange = (value: string) => {
    const sortBy = value as SortOption;
    setSortOption(sortBy);
    
    const sortedMunicipalities = sortMunicipalities(filteredMunicipalities, sortBy);
    setFilteredMunicipalities(sortedMunicipalities);
    
    // Lagre tilstand
    saveState();
  };

  // Hjelpefunksjon for å utføre søk
  const performSearch = (term: string, municipalities: Municipality[] = allMunicipalities) => {
    const termLower = term.toLowerCase().trim();
    
    if (!termLower) {
      return municipalities;
    }
    
    const results = municipalities.filter(municipality => {
      const nameLower = municipality.name.toLowerCase();
      
      // Direct match in name
      if (nameLower.includes(termLower)) {
        return true;
      }
      
      // Match with "kommune" appended if not already there
      if (!nameLower.includes("kommune") && 
          (`${nameLower} kommune`).includes(termLower)) {
        return true;
      }
      
      // Match without "kommune" if it's in the name
      if (nameLower.includes("kommune") && 
          nameLower.replace(" kommune", "").includes(termLower)) {
        return true;
      }
      
      // Split searches like "søndre land" into words
      const searchWords = termLower.split(/\s+/);
      if (searchWords.length > 1) {
        // Check if ALL words in the search are in the municipality name (in any order)
        const allWordsMatch = searchWords.every(word => 
          nameLower.includes(word) || 
          word === "kommune" || 
          word.length < 3 // Skip very short words
        );
        if (allWordsMatch) return true;
      }
      
      return false;
    });
    
    return results;
  };
  
  // Hjelpefunksjon for fylkesfiltrering
  const performCountyFilter = (county: string | null, municipalities: Municipality[] = allMunicipalities) => {
    if (county === null) {
      return municipalities;
    }
    
    const filtered = municipalities.filter(
      municipality => municipality.county === county
    );
    
    // Sorter etter kommunenavn
    return filtered.sort((a, b) => a.name.localeCompare(b.name, 'nb-NO'));
  };
  
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      toast.info("Skriv inn et søkeord for å finne kommuner");
      return;
    }
    
    const searchResults = performSearch(searchTerm);
    
    setFilteredMunicipalities(sortMunicipalities(searchResults, sortOption));
    setViewMode('searchResults');
    setCurrentPage(1);
    
    // Nullstill fylkesfilteret når man gjør et søk
    setSelectedCounty(null);
    
    if (searchResults.length === 0) {
      toast.info("Ingen kommuner funnet med dette søket. Prøv andre søkeord.");
    } else {
      console.log(`Fant ${searchResults.length} kommuner som matcher søket "${searchTerm}"`);
    }
    
    // Lagre søketilstand
    saveState();
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCountySelect = (county: string | null) => {
    setSelectedCounty(county);
    setSearchTerm(''); // Nullstill søketermen når man bytter fylke
    
    if (county === null) {
      const sortedResults = sortMunicipalities(allMunicipalities, sortOption);
      setFilteredMunicipalities(sortedResults);
      setViewMode('allMunicipalities');
    } else {
      const countyMunicipalities = performCountyFilter(county);
      const sortedResults = sortMunicipalities(countyMunicipalities, sortOption);
      setFilteredMunicipalities(sortedResults);
      setViewMode('countyFiltered');
      
      // Gi tilbakemelding om resultatet
      if (countyMunicipalities.length === 0) {
        toast.info(`Ingen kommuner funnet i ${county}. API-et kan ha begrenset data for dette fylket.`);
      } else {
        if (allMunicipalities.length < 300) {
          toast.info(`Viser ${countyMunicipalities.length} kommuner i ${county}. Merk at ikke alle kommuner i fylket nødvendigvis er tilgjengelige.`);
        }
      }
    }
    
    // Tilbakestill til første side
    setCurrentPage(1);
    
    // Lagre tilstand
    saveState();
  };

  const showAllMunicipalities = () => {
    const sortedResults = sortMunicipalities(allMunicipalities, sortOption);
    setFilteredMunicipalities(sortedResults);
    setViewMode('allMunicipalities');
    setCurrentPage(1);
    setSelectedCounty(null);
    setSearchTerm('');
    
    // Lagre tilstand
    saveState();
  };

  // Calculate pagination values
  const indexOfLastMunicipality = currentPage * municipalitiesPerPage;
  const indexOfFirstMunicipality = indexOfLastMunicipality - municipalitiesPerPage;
  const currentMunicipalities = filteredMunicipalities.slice(
    indexOfFirstMunicipality,
    indexOfLastMunicipality
  );
  const totalPages = Math.ceil(filteredMunicipalities.length / municipalitiesPerPage);

  // Handle pagination
  const paginate = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: document.getElementById('municipality-list')?.offsetTop || 0, behavior: 'smooth' });
      
      // Lagre sidetilstand
      saveState();
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if there are fewer than maxVisiblePages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate the range around current page
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're near the start
      if (currentPage <= 3) {
        endPage = Math.min(maxVisiblePages - 1, totalPages - 1);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - maxVisiblePages + 2);
      }
      
      // Add ellipsis if needed before the range
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
      
      // Add the range
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis if needed after the range
      if (endPage < totalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };
  
  const latestYear = getLatestYear();
  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      <Seo
        title={`Kommunale avgifter i Norge ${latestYear} | Sammenlign vann, avløp, renovasjon og septik`}
        description={`Finn og sammenlign kommunale avgifter mellom alle norske kommuner. Se priser for vann, avløp, renovasjon og septiktømming for en standardbolig. Oppdatert med ${latestYear}-tall.`}
        path="/"
      />
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero – samme kortstil som kommunedetalj */}
        <section className="mb-8">
          <div className="py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
            <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
              <span className="text-nordicBlue font-semibold">Forside</span>
            </nav>
            <div className="mb-4">
              <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">
                Kommunale avgifter i Norge
              </h1>
              <p className="text-warmGray">
                Sammenlign kommunale avgifter for en standardbolig på 120 m² mellom norske kommuner og se utviklingen over tid.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-3 h-5 w-5 text-warmGray" />
                <Input
                  type="text"
                  placeholder="Søk etter kommune..."
                  className={`pl-10 py-6 pr-4 bg-white w-full ${viewMode === 'searchResults' && searchTerm ? 'ring-2 ring-nordicBlue/40 border-nordicBlue' : ''}`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                {searchTerm.trim().length >= 1 && allMunicipalities.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-nordicBlue/10 rounded-lg shadow-lg max-h-56 overflow-auto">
                    {(() => {
                      const all = performSearch(searchTerm);
                      const suggestions = all.slice(0, 10);
                      return suggestions.length > 0 ? (
                        <>
                          {suggestions.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              className="w-full text-left px-4 py-3 hover:bg-nordicBlue/5 focus:bg-nordicBlue/5 focus:outline-none flex justify-between items-center"
                              onClick={() => {
                                setSearchTerm('');
                                navigate(`/municipality/${m.id}`);
                              }}
                            >
                              <span className="font-medium">{m.name}</span>
                              <span className="text-sm text-warmGray">{m.county}</span>
                            </button>
                          ))}
                          {all.length > 10 && (
                            <div className="px-4 py-2 text-xs text-warmGray border-t">
                              +{all.length - 10} flere – trykk Enter for å søke
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="px-4 py-3 text-sm text-warmGray">
                          Ingen treff. Prøv et annet søkeord.
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <Button
                className="bg-nordicBlue hover:bg-nordicBlue/90 py-6 px-6"
                onClick={handleSearch}
              >
                Søk
              </Button>
            </div>
          </div>
        </section>
        
        {/* Stats Summary */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Nøkkeltall</h2>
          <StatsSummary />
        </section>
        
        {/* Municipality Cards */}
        <section id="municipality-list">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h2 className="text-2xl font-semibold">Kommunale avgifter</h2>
              {viewMode === 'countyFiltered' && selectedCounty && (
                <p className="text-warmGray mt-1">
                  Viser alle kommuner i {selectedCounty} ({filteredMunicipalities.length})
                  {selectedYear !== getLatestYear() && ` — tall for ${selectedYear}`}
                </p>
              )}
              {viewMode === 'searchResults' && (
                <p className="text-warmGray mt-1">
                  Søkeresultater for "{searchTerm}" ({filteredMunicipalities.length} treff)
                  {selectedYear !== getLatestYear() && ` — tall for ${selectedYear}`}
                </p>
              )}
              {viewMode === 'allMunicipalities' && (
                <p className="text-warmGray mt-1">
                  Viser alle kommuner ({filteredMunicipalities.length})
                  {selectedYear !== getLatestYear() && ` — tall for ${selectedYear}`}
                </p>
              )}
            </div>
            
            <div className="flex flex-wrap flex-col md:flex-row items-end md:items-center gap-3 p-4 rounded-lg bg-white border border-slate-200 shadow-sm">
              {viewMode === 'searchResults' && searchTerm.trim() && (
                <Badge variant="default" className="bg-nordicBlue/90 hover:bg-nordicBlue gap-1.5 py-1.5 px-3">
                  <span>Søk: &quot;{searchTerm}&quot;</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      const sortedResults = sortMunicipalities(allMunicipalities, sortOption);
                      setFilteredMunicipalities(sortedResults);
                      setViewMode('allMunicipalities');
                      setCurrentPage(1);
                      saveState();
                    }}
                    className="hover:bg-white/20 rounded p-0.5 -mr-1"
                    aria-label="Fjern søkefilter"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </Badge>
              )}
              <YearSelector value={selectedYear} onChange={setSelectedYear} />
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-deepCharcoal">Fylke:</span>
                <CountySelector
                  municipalities={allMunicipalities}
                  onCountySelect={handleCountySelect}
                  value={selectedCounty}
                />
              </div>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-deepCharcoal">Sortering:</span>
                <Select value={sortOption} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-[180px] bg-white border-slate-200">
                    <SelectValue placeholder="Velg sortering" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fee-desc">Avgift (Høyest først)</SelectItem>
                    <SelectItem value="fee-asc">Avgift (Lavest først)</SelectItem>
                    <SelectItem value="name-asc">Navn (A-Å)</SelectItem>
                    <SelectItem value="name-desc">Navn (Å-A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(viewMode !== 'allMunicipalities' || selectedCounty !== null) && (
                <Button variant="outline" onClick={showAllMunicipalities} className="border-slate-200">
                  Se alle kommuner
                </Button>
              )}
            </div>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                  <Skeleton className="h-7 w-32 mb-2" />
                  <Skeleton className="h-4 w-24 mb-4" />
                  <Skeleton className="h-8 w-40 mb-2" />
                  <Skeleton className="h-4 w-36 mb-6" />
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {filteredMunicipalities.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentMunicipalities.map(municipality => (
                    <MunicipalityCard 
                      key={municipality.id} 
                      municipality={municipality} 
                      allMunicipalities={allMunicipalities}
                      selectedYear={selectedYear}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center p-8 bg-white rounded-lg shadow-sm">
                  <p className="text-lg text-warmGray">Ingen kommuner funnet som matcher dine kriterier.</p>
                </div>
              )}
              
              {filteredMunicipalities.length > municipalitiesPerPage && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious onClick={() => paginate(currentPage - 1)} />
                      </PaginationItem>
                    )}
                    
                    {getPageNumbers().map((page, index) => {
                      if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                        return (
                          <PaginationItem key={`ellipsis-${index}`}>
                            <PaginationLink>...</PaginationLink>
                          </PaginationItem>
                        );
                      }
                      
                      return (
                        <PaginationItem key={`page-${page}`}>
                          <PaginationLink
                            isActive={currentPage === page}
                            onClick={() => paginate(Number(page))}
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    {currentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext onClick={() => paginate(currentPage + 1)} />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </section>
        
        {/* About Section */}
        <section className="mt-16 bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-2xl font-semibold mb-4">Om kommunale avgifter</h2>
          <p className="text-warmGray mb-4">
            Kommunale avgifter er gebyrer som innkreves av kommunen for tjenester som vann, avløp, renovasjon 
            og eventuelt eiendomsskatt. Disse avgiftene varierer fra kommune til kommune og kan utgjøre en 
            betydelig del av boligkostnadene.
          </p>
          <p className="text-warmGray">
            For en standardbolig på 120m², som er brukt som utgangspunkt i denne visualiseringen, ser vi store 
            forskjeller mellom kommunene i Norge. Dette verktøyet lar deg utforske og sammenligne disse forskjellene.
          </p>
          <p className="text-warmGray mt-4">
            <small>Kilde: Statistisk sentralbyrå (SSB), tabell 12842: Kommunale gebyrer knyttet til bolig</small>
          </p>
        </section>
        
        {/* New sections for Environmental Impact and Saving Tips */}
        <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <EnvironmentalImpactCalculator className="h-full" />
          <SavingTipsCard tips={savingTips} className="h-full" />
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
